/* eslint max-classes-per-file: 0 */

import { BaseCollectorApiAuth } from '@/api/BaseApiAuth'
import { AxiosRequestConfig } from 'axios'
import { BaseApiParams } from '@/typings/common'

export interface OcrTaskStatus {
  task_status: 'DONE' | 'PENDING' | 'IN_PROGRESS' | 'ERROR';
  task_id: string;
  file_sha1_sum: string;
  file_filename: string;
  created_at: string;
  errors_count: number;
  ready_count: number;
}

export interface OcrStatusResponse {
  data: OcrTaskStatus[];
}

export type OcrResultStatus = 'ready' | 'error' | 'manual_check';

export interface OcrResultInDb {
  id: number;
  task_id: string;
  form_data_id: number;
  lk_publication_id: number;
  participant_code?: number;
  status: OcrResultStatus;
  recognized_data: Record<string, unknown>;
  error_message?: string;
  file_sha1_sum: string;
  file_filename: string;
  created_at: string;
  updated_at: string;
}

export interface GroupedOcrResultInDb extends OcrResultInDb {
  grouped?: boolean;
}

export interface OcrResultsParams extends BaseApiParams {
  page?: number;
  per_page?: number;
  status?: OcrResultStatus;
  task_id?: string;
  participant_code?: number;
  order_by?: string;
  error_messages?: string[];
}

export interface UpdateOcrResultParams {
  status?: string;
  values?: (string | number)[];
  participant_code?: number;
}

export interface BatchUpdateOcrResultsBody {
  ocr_result_ids: string[];
  status: string;
  error_message?: string;
}

export class CollectorOcrApi extends BaseCollectorApiAuth<
  OcrTaskStatus | OcrResultInDb | GroupedOcrResultInDb,
  OcrTaskStatus | OcrResultInDb | GroupedOcrResultInDb
> {
  protected readonly endpoint = 'ocr_results';

  protected readonly hasFinishSlash = false;

  public getStatus(
    formDataId: string | number,
    lkPublicationId: string | number,
    config: AxiosRequestConfig = {},
  ) {
    const path = `${formDataId}/status`
    const [url] = this.getUrl(path)
    const params = {
      lk_publication_id: lkPublicationId,
    }
    return this.request('GET', url, { params, ...config })
  }

  public getResults(
    formDataId: string | number,
    lkPublicationId: string | number,
    params: OcrResultsParams = {},
    config: AxiosRequestConfig = {},
  ) {
    const path = `${formDataId}/results`
    const [url] = this.getUrl(path)
    const queryParams = {
      lk_publication_id: lkPublicationId,
      page: params.page || 1,
      per_page: params.per_page || 10,
      ...(params.status && { status: params.status }),
      ...(params.task_id && { task_id: params.task_id }),
      ...(params.participant_code && { participant_code: params.participant_code }),
      ...(params.order_by && { order_by: params.order_by }),
      ...(params.error_messages && { error_messages: params.error_messages }),
    }
    return this.request('GET', url, {
      params: queryParams,
      paramsSerializer: (p) => {
        const search = new URLSearchParams()
        Object.entries(p).forEach(([key, value]) => {
          if (Array.isArray(value)) {
            value.forEach((v) => search.append(key, v))
          } else if (value !== null && value !== undefined) {
            search.append(key, String(value))
          }
        })
        return search.toString()
      },
      ...config,
    })
  }

  public updateOcrResult(
    ocrResultId: string | number,
    data: UpdateOcrResultParams,
    config: AxiosRequestConfig = {},
  ) {
    const path = `${ocrResultId}`
    const [url] = this.getUrl(path)
    return this.request('POST', url, { data, ...config })
  }

  public batchUpdateOcrResults(
    data: BatchUpdateOcrResultsBody,
    config: AxiosRequestConfig = {},
  ) {
    const path = 'batch-update'
    const [url] = this.getUrl(path)
    return this.request('POST', url, { data, ...config })
  }

  public getErrorMessages(
    formDataId: string | number,
    config: AxiosRequestConfig = {},
  ) {
    const path = `${formDataId}/error_messages`
    const [url] = this.getUrl(path)
    return this.request('GET', url, config)
  }

  public deleteFsdOcrData(
    formDataId: string | number,
    taskId?: string,
    ocrResultId?: string | number,
    config: AxiosRequestConfig = {},
  ) {
    const path = `${formDataId}/delete`
    const [url] = this.getUrl(path)
    const params: Record<string, string | number> = {}
    if (taskId) params.task_id = taskId
    if (ocrResultId) params.ocr_result_id = ocrResultId
    return this.request('POST', url, { params, ...config })
  }

  public getInstruction(config: AxiosRequestConfig = {}) {
    const [url] = this.getUrl('instruction')
    return this.request<never, Blob>('GET', url, { responseType: 'blob', ...config })
  }
}
